import { useEffect, useRef, useState } from 'react';
import type { LetterSignature } from '../types';

type Point = { x: number; y: number };

type Props = {
  onDone: (sig: LetterSignature) => void;
  onCancel: () => void;
  inkColor?: string;
};

/**
 * Full-screen signature capture. Draw with finger/stylus/mouse; we smooth
 * the strokes with quadratic midpoint interpolation (the standard
 * signature-pad technique) and export a normalized SVG path tight to the
 * bounding box of the ink, so it renders crisply at any size on the letter.
 */
export function SignaturePad({ onDone, onCancel, inkColor = '#2b2a26' }: Props) {
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
  const strokesRef = useRef<Point[][]>([]);
  const drawingRef = useRef(false);
  const [hasInk, setHasInk] = useState(false);
  const [portrait, setPortrait] = useState(
    typeof window !== 'undefined' && window.innerHeight > window.innerWidth,
  );

  useEffect(() => {
    const onResize = () => setPortrait(window.innerHeight > window.innerWidth);
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  // Auto-rotate to landscape so there's room to sign. Needs fullscreen
  // first (the orientation lock API requires it). Works on Android Chrome;
  // iOS Safari doesn't support orientation lock, so there we fall back to
  // the "turn your phone" hint. Everything is wrapped in try/catch — a
  // failed lock just leaves the pad portrait, which still works.
  useEffect(() => {
    let locked = false;
    const orientation = screen.orientation as
      | (ScreenOrientation & { lock?: (o: string) => Promise<void> })
      | undefined;
    (async () => {
      try {
        const el = overlayRef.current;
        if (el?.requestFullscreen) {
          await el.requestFullscreen({ navigationUI: 'hide' }).catch(() => undefined);
        }
        if (orientation?.lock) {
          // landscape-secondary = the other landscape (rotate the opposite
          // way), which is the natural turn for most right-handed signing.
          await orientation.lock('landscape-secondary');
          locked = true;
        }
      } catch {
        // unsupported (iOS) — the in-pad hint covers it
      }
    })();
    return () => {
      try {
        if (locked) orientation?.unlock?.();
      } catch {
        // ignore
      }
      try {
        if (document.fullscreenElement) void document.exitFullscreen();
      } catch {
        // ignore
      }
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const setup = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.lineWidth = 2.6;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = inkColor;
      ctxRef.current = ctx;
      redraw();
    };
    setup();
    window.addEventListener('resize', setup);
    return () => window.removeEventListener('resize', setup);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inkColor]);

  function posFromEvent(e: React.PointerEvent): Point {
    const rect = canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  function onPointerDown(e: React.PointerEvent) {
    e.preventDefault();
    drawingRef.current = true;
    try {
      canvasRef.current?.setPointerCapture(e.pointerId);
    } catch {
      // ignore
    }
    strokesRef.current.push([posFromEvent(e)]);
  }

  function onPointerMove(e: React.PointerEvent) {
    if (!drawingRef.current) return;
    const stroke = strokesRef.current[strokesRef.current.length - 1];
    stroke.push(posFromEvent(e));
    if (!hasInk) setHasInk(true);
    redraw();
  }

  function onPointerUp() {
    drawingRef.current = false;
  }

  function redraw() {
    const ctx = ctxRef.current;
    const canvas = canvasRef.current;
    if (!ctx || !canvas) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
    for (const stroke of strokesRef.current) drawStroke(ctx, stroke);
  }

  function clear() {
    strokesRef.current = [];
    setHasInk(false);
    redraw();
  }

  function done() {
    const sig = strokesToSignature(strokesRef.current);
    if (!sig) {
      onCancel();
      return;
    }
    onDone(sig);
  }

  return (
    <div
      ref={overlayRef}
      className="sig-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Sign your letter"
    >
      <div className="sig-head">
        <button type="button" className="btn btn-ghost btn-small" onClick={onCancel}>
          Cancel
        </button>
        <span className="sig-title">Sign with your finger</span>
        <button
          type="button"
          className="btn btn-primary btn-small"
          onClick={done}
          disabled={!hasInk}
        >
          Use signature
        </button>
      </div>
      <div className="sig-canvas-wrap">
        <canvas
          ref={canvasRef}
          className="sig-canvas"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
          onPointerCancel={onPointerUp}
        />
        <div className="sig-baseline" aria-hidden="true" />
        {!hasInk && (
          <span className="sig-hint">
            Draw your signature here
            {portrait && (
              <span className="sig-hint-flip">
                Turn your phone sideways for more room ↻
              </span>
            )}
          </span>
        )}
      </div>
      <div className="sig-foot">
        <button type="button" className="btn btn-ghost btn-small" onClick={clear} disabled={!hasInk}>
          Clear
        </button>
        <span className="sig-foot-note">It'll sit at the bottom of your letter.</span>
      </div>
    </div>
  );
}

function midpoint(a: Point, b: Point): Point {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

/** Live canvas render of one stroke, smoothed with quadratic midpoints. */
function drawStroke(ctx: CanvasRenderingContext2D, pts: Point[]) {
  if (pts.length === 0) return;
  if (pts.length === 1) {
    // A tap = a dot.
    ctx.beginPath();
    ctx.arc(pts[0].x, pts[0].y, ctx.lineWidth / 2, 0, Math.PI * 2);
    ctx.fillStyle = ctx.strokeStyle as string;
    ctx.fill();
    return;
  }
  ctx.beginPath();
  ctx.moveTo(pts[0].x, pts[0].y);
  for (let i = 1; i < pts.length - 1; i++) {
    const mid = midpoint(pts[i], pts[i + 1]);
    ctx.quadraticCurveTo(pts[i].x, pts[i].y, mid.x, mid.y);
  }
  const last = pts[pts.length - 1];
  ctx.lineTo(last.x, last.y);
  ctx.stroke();
}

/**
 * Convert captured strokes into a normalized SVG path. Computes the
 * bounding box of all points, offsets the path to that box's origin (with
 * a small padding), and returns { d, w, h } so the consumer can drop it
 * into <svg viewBox="0 0 w h"><path d={d} /></svg>.
 */
function strokesToSignature(strokes: Point[][]): LetterSignature | null {
  const all = strokes.flat();
  if (all.length === 0) return null;

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of all) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  const pad = 6;
  const ox = minX - pad;
  const oy = minY - pad;
  const w = Math.max(1, Math.round(maxX - minX + pad * 2));
  const h = Math.max(1, Math.round(maxY - minY + pad * 2));

  const r = (n: number) => Math.round((n) * 10) / 10;
  const parts: string[] = [];
  for (const stroke of strokes) {
    if (stroke.length === 0) continue;
    if (stroke.length === 1) {
      const p = stroke[0];
      // Tiny dash so a single tap still shows up.
      parts.push(`M ${r(p.x - ox)} ${r(p.y - oy)} l 0.6 0`);
      continue;
    }
    parts.push(`M ${r(stroke[0].x - ox)} ${r(stroke[0].y - oy)}`);
    for (let i = 1; i < stroke.length - 1; i++) {
      const c = stroke[i];
      const mid = midpoint(stroke[i], stroke[i + 1]);
      parts.push(`Q ${r(c.x - ox)} ${r(c.y - oy)} ${r(mid.x - ox)} ${r(mid.y - oy)}`);
    }
    const last = stroke[stroke.length - 1];
    parts.push(`L ${r(last.x - ox)} ${r(last.y - oy)}`);
  }

  return { d: parts.join(' '), w, h };
}
